import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { promptAPI, adminAPI, ebayAPI, channelAPI } from '@/services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { capitalize } from '@/lib/utils';
import { 
  Key, 
  Sparkles, 
  Sliders, 
  Mail, 
  CheckCircle2, 
  Copy, 
  Eye, 
  EyeOff, 
  Save, 
  RotateCcw, 
  Search, 
  FileCode, 
  Cpu, 
  Zap, 
  ShieldCheck, 
  Plus, 
  Loader2,
  ShoppingCart,
  RefreshCw,
  ExternalLink,
  Trash2
} from 'lucide-react';
import type { PromptTemplate } from '@/types';

export default function Settings() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'prompts' | 'api' | 'automation' | 'channels'>('prompts');
  const [selectedPrompt, setSelectedPrompt] = useState<PromptTemplate | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedPrompt, setEditedPrompt] = useState('');
  const [promptSearch, setPromptSearch] = useState('');

  const [geminiApiKey, setGeminiApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [isTestingKey, setIsTestingKey] = useState(false);
  const [isSyncingEbay, setIsSyncingEbay] = useState(false);

  const [confidenceThreshold, setConfidenceThreshold] = useState(70);
  const [pollInterval, setPollInterval] = useState('60');
  const [selectedModel, setSelectedModel] = useState('gemini-2.0-flash');

  // Connected email accounts (editable)
  const [newEmail, setNewEmail] = useState('');
  const [newProvider, setNewProvider] = useState('gmail');
  const [newAppPassword, setNewAppPassword] = useState('');
  const [isAddingEmail, setIsAddingEmail] = useState(false);

  const { data: connectedEmails = [] } = useQuery({
    queryKey: ['connectedEmails'],
    queryFn: async () => {
      const res = await channelAPI.listEmails();
      return res.data || [];
    },
  });

  const handleAddEmail = async () => {
    if (!newEmail.trim()) {
      toast.error('Enter an email address');
      return;
    }
    setIsAddingEmail(true);
    try {
      await channelAPI.addEmail(newEmail.trim(), newProvider, newAppPassword.trim() || undefined);
      toast.success('Email account connected');
      setNewEmail('');
      setNewAppPassword('');
      queryClient.invalidateQueries({ queryKey: ['connectedEmails'] });
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to add email account');
    } finally {
      setIsAddingEmail(false);
    }
  };

  const handleRemoveEmail = async (id: string, email: string) => {
    if (!window.confirm(`Remove ${email}?`)) return;
    try {
      await channelAPI.removeEmail(id);
      toast.success('Email account removed');
      queryClient.invalidateQueries({ queryKey: ['connectedEmails'] });
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to remove email account');
    }
  };

  const handleToggleEmail = async (id: string, active: boolean) => {
    try {
      await channelAPI.setActive(id, !active);
      queryClient.invalidateQueries({ queryKey: ['connectedEmails'] });
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Failed to update email account');
    }
  };

  const { data: currentApiKey = '' } = useQuery({
    queryKey: ['geminiApiKey'],
    queryFn: async () => {
      try {
        const response = await adminAPI.getKey('GEMINI_API_KEY');
        return response.data.key_value || '';
      } catch (error) {
        return '';
      }
    },
  });

  const { data: prompts = [], isLoading: isLoadingPrompts } = useQuery({
    queryKey: ['prompts'],
    queryFn: async () => {
      const response = await promptAPI.list({});
      const data = response.data || [];
      if (data.length > 0 && !selectedPrompt) {
        setSelectedPrompt(data[0]);
      }
      return data;
    },
  });

  const availableTokens = [
    { token: '{customer_name}', label: 'Customer Name' },
    { token: '{order_id}', label: 'Order ID' },
    { token: '{tracking_number}', label: 'Tracking No.' },
    { token: '{product_name}', label: 'Product Name' },
    { token: '{refund_amount}', label: 'Refund Amount' },
    { token: '{expected_delivery}', label: 'Delivery Date' },
    { token: '{issue_summary}', label: 'Issue Summary' },
  ];

  const handleInjectToken = (tokenStr: string) => {
    setEditedPrompt((prev) => prev + ` ${tokenStr} `);
    toast.success(`Inserted ${tokenStr}`);
  };

  const handleSavePrompt = async () => {
    if (!selectedPrompt) return;
    
    try {
      await promptAPI.update(selectedPrompt.id, { prompt_template: editedPrompt });
      toast.success('Prompt template saved successfully');
      setIsEditing(false);
      queryClient.invalidateQueries({ queryKey: ['prompts'] });
    } catch (error) {
      toast.error('Failed to update prompt template');
    }
  };

  const handleCopyPromptText = () => {
    const textToCopy = isEditing ? editedPrompt : selectedPrompt?.prompt_template || '';
    navigator.clipboard.writeText(textToCopy);
    toast.success('Prompt copied to clipboard!');
  };

  const maskApiKey = (key: string) => {
    if (!key) return 'Not configured';
    if (key.length <= 8) return '••••••••';
    return key.slice(0, 4) + '••••••••' + key.slice(-4);
  };

  const handleUpdateApiKey = async () => {
    if (!geminiApiKey.trim()) {
      toast.error('Please enter a valid API key');
      return;
    }
    
    try {
      await adminAPI.updateKey('GEMINI_API_KEY', geminiApiKey, 'Google Gemini API Key');
      toast.success('Gemini API key updated successfully');
      queryClient.invalidateQueries({ queryKey: ['geminiApiKey'] });
      setGeminiApiKey('');
      setShowApiKey(false);
    } catch (error) {
      toast.error('Failed to update API key');
    }
  };

  const handleTestApiKey = async () => {
    setIsTestingKey(true);
    try {
      await adminAPI.reloadGeminiKey();
      toast.success('API Key connection validated successfully!');
    } catch (e) {
      toast.error('API key connection test failed');
    } finally {
      setIsTestingKey(false);
    }
  };

  const handleConnectEbay = async () => {
    try {
      const res = await ebayAPI.getAuthUrl();
      if (res.data?.authorization_url) {
        window.open(res.data.authorization_url, '_blank');
        toast.success('eBay authorization window opened. Complete login in browser.');
      }
    } catch (e) {
      toast.error('Failed to get eBay authorization URL');
    }
  };

  const handleTestEbay = async () => {
    try {
      const res = await ebayAPI.testConnection();
      toast.success(res.data?.message || 'eBay API connection active!');
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'eBay API test failed');
    }
  };

  const handleSyncEbay = async () => {
    setIsSyncingEbay(true);
    try {
      const res = await ebayAPI.sync(50);
      toast.success(`eBay Sync Complete: ${res.data?.messages_processed || 0} messages processed`);
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'eBay message sync failed');
    } finally {
      setIsSyncingEbay(false);
    }
  };

  const filteredPrompts = prompts.filter((p: PromptTemplate) =>
    p.category.toLowerCase().includes(promptSearch.toLowerCase()) ||
    p.prompt_template.toLowerCase().includes(promptSearch.toLowerCase())
  );

  return (
    <div className="p-6 max-w-[1500px] mx-auto space-y-6 bg-slate-50/50 min-h-screen">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2.5">
            System Settings & AI Prompts
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Configure Gemini AI models, prompt templates, confidence thresholds, and email channel settings
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-3 py-1 text-xs font-semibold rounded-full bg-blue-50 text-blue-700 border border-blue-200 flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" />
            Active Version 1.0.0
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2 border-b border-gray-200 pb-2">
        <button
          onClick={() => setActiveTab('prompts')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all ${
            activeTab === 'prompts'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
          }`}
        >
          <Sparkles className="w-4 h-4" />
          <span>AI Response Prompts</span>
        </button>

        <button
          onClick={() => setActiveTab('api')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all ${
            activeTab === 'api'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
          }`}
        >
          <Key className="w-4 h-4" />
          <span>Gemini API & Models</span>
        </button>

        <button
          onClick={() => setActiveTab('automation')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all ${
            activeTab === 'automation'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
          }`}
        >
          <Sliders className="w-4 h-4" />
          <span>Automation Rules</span>
        </button>

        <button
          onClick={() => setActiveTab('channels')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all ${
            activeTab === 'channels'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
          }`}
        >
          <Mail className="w-4 h-4" />
          <span>Email Channels</span>
        </button>
      </div>

      {activeTab === 'prompts' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-4 space-y-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search templates by category..."
                value={promptSearch}
                onChange={(e) => setPromptSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-xs bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-blue-500 shadow-2xs"
              />
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden divide-y divide-gray-100">
              <div className="p-3.5 bg-gray-50/70 border-b border-gray-100 flex items-center justify-between">
                <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Category Templates
                </span>
                <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-blue-100 text-blue-800">
                  {filteredPrompts.length} Loaded
                </span>
              </div>

              {isLoadingPrompts ? (
                <div className="p-8 text-center">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-600" />
                  <p className="text-xs text-gray-500 mt-2">Loading prompts...</p>
                </div>
              ) : (
                <div className="max-h-[550px] overflow-y-auto divide-y divide-gray-100">
                  {filteredPrompts.map((prompt: PromptTemplate) => {
                    const isSelected = selectedPrompt?.id === prompt.id;
                    const isSystem = prompt.category === 'system';

                    return (
                      <div
                        key={prompt.id}
                        onClick={() => {
                          setSelectedPrompt(prompt);
                          setIsEditing(false);
                        }}
                        className={`p-3.5 cursor-pointer transition-all flex items-center justify-between group ${
                          isSelected
                            ? 'bg-blue-50/80 border-l-4 border-blue-600'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${isSystem ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                            {isSystem ? <Cpu className="w-4 h-4" /> : <FileCode className="w-4 h-4" />}
                          </div>
                          <div>
                            <h4 className="text-xs font-bold text-gray-900 capitalize">
                              {isSystem ? 'Global System Instruction' : prompt.category.replace(/_/g, ' ')}
                            </h4>
                            <p className="text-[11px] text-gray-500 truncate max-w-[180px] mt-0.5">
                              {prompt.prompt_template.slice(0, 45)}...
                            </p>
                          </div>
                        </div>

                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                          prompt.language === 'en' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-amber-50 text-amber-700 border border-amber-200'
                        }`}>
                          {prompt.language ? prompt.language.toUpperCase() : 'EN'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-8">
            {selectedPrompt ? (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-gray-100">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-bold text-gray-900 capitalize">
                        {selectedPrompt.category === 'system' 
                          ? 'Global System Instruction' 
                          : capitalize(selectedPrompt.category.replace(/_/g, ' '))}
                      </h3>
                      <span className="px-2.5 py-0.5 text-xs font-bold bg-blue-100 text-blue-800 rounded-full">
                        {selectedPrompt.language?.toUpperCase() || 'EN'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Controls Gemini AI response style, structure, and tone for this scenario
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      onClick={handleCopyPromptText}
                      variant="outline"
                      size="sm"
                      className="text-xs flex items-center gap-1.5"
                    >
                      <Copy className="w-3.5 h-3.5" />
                      <span>Copy</span>
                    </Button>

                    {!isEditing ? (
                      <Button
                        onClick={() => {
                          setEditedPrompt(selectedPrompt.prompt_template);
                          setIsEditing(true);
                        }}
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 text-white text-xs flex items-center gap-1.5"
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                        <span>Edit Template</span>
                      </Button>
                    ) : (
                      <Button
                        onClick={() => setIsEditing(false)}
                        variant="outline"
                        size="sm"
                        className="text-xs border-gray-300"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        <span>Cancel</span>
                      </Button>
                    )}
                  </div>
                </div>

                {isEditing && (
                  <div className="p-3 bg-blue-50/50 border border-blue-100 rounded-xl space-y-2">
                    <span className="text-[11px] font-bold text-blue-900 uppercase tracking-wider block">
                      Insert a placeholder
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {availableTokens.map((item) => (
                        <button
                          key={item.token}
                          onClick={() => handleInjectToken(item.token)}
                          className="px-2.5 py-1 text-xs font-mono font-semibold bg-white border border-blue-200 text-blue-700 rounded-lg hover:bg-blue-600 hover:text-white transition-colors flex items-center gap-1 shadow-2xs"
                        >
                          <Plus className="w-3 h-3" />
                          <span>{item.token}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {isEditing ? (
                  <div className="space-y-3">
                    <textarea
                      value={editedPrompt}
                      onChange={(e) => setEditedPrompt(e.target.value)}
                      rows={14}
                      className="w-full p-4 text-xs font-mono bg-slate-900 text-slate-100 border border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none leading-relaxed shadow-inner"
                    />
                    <div className="flex items-center justify-between pt-2">
                      <span className="text-xs text-gray-500 font-mono">
                        {editedPrompt.length} Characters · {editedPrompt.split(/\s+/).filter(Boolean).length} Words
                      </span>
                      <Button
                        onClick={handleSavePrompt}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center gap-1.5 px-4 py-2"
                      >
                        <Save className="w-4 h-4" />
                        <span>Save Changes</span>
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="p-5 bg-slate-900 text-slate-100 rounded-xl border border-slate-800 font-mono text-xs leading-relaxed overflow-x-auto shadow-inner">
                    <pre className="whitespace-pre-wrap font-sans text-slate-200">
                      {selectedPrompt.prompt_template}
                    </pre>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white p-12 rounded-2xl border border-gray-100 text-center">
                <FileCode className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm font-semibold text-gray-600">Select a prompt template to view or edit</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'api' && (
        <div className="space-y-6 max-w-4xl">
          <Card className="border-gray-100 shadow-sm">
            <CardHeader className="border-b border-gray-100">
              <CardTitle className="text-base font-bold text-gray-900 flex items-center gap-2">
                <Key className="w-5 h-5 text-blue-600" />
                Google Gemini API Key
              </CardTitle>
              <CardDescription className="text-xs">
                Manage your API key for Google Gemini AI automated responses
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-5 space-y-4">
              <div className="flex items-center justify-between p-3.5 bg-gray-50 rounded-xl border border-gray-200/70">
                <div>
                  <span className="text-xs font-semibold text-gray-500 block">Configured API Key</span>
                  <code className="text-sm font-mono font-bold text-gray-900 mt-0.5 block">
                    {showApiKey ? currentApiKey : maskApiKey(currentApiKey)}
                  </code>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => setShowApiKey(!showApiKey)}
                    variant="outline"
                    size="sm"
                    className="text-xs"
                  >
                    {showApiKey ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    <span className="ml-1">{showApiKey ? 'Hide' : 'Reveal'}</span>
                  </Button>

                  <Button
                    onClick={handleTestApiKey}
                    variant="outline"
                    size="sm"
                    className="text-xs border-blue-200 text-blue-700 hover:bg-blue-50"
                    disabled={isTestingKey}
                  >
                    {isTestingKey ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                    <span className="ml-1">Test Connection</span>
                  </Button>
                </div>
              </div>

              <div className="space-y-2 pt-2">
                <label className="text-xs font-bold text-gray-700">Update API Key</label>
                <div className="flex gap-2">
                  <Input
                    type="password"
                    value={geminiApiKey}
                    onChange={(e) => setGeminiApiKey(e.target.value)}
                    placeholder="Paste new AIzaSy... key here"
                    className="text-xs font-mono"
                  />
                  <Button
                    onClick={handleUpdateApiKey}
                    className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-4"
                  >
                    Save Key
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-gray-100 shadow-sm">
            <CardHeader className="border-b border-gray-100">
              <CardTitle className="text-base font-bold text-gray-900 flex items-center gap-2">
                <Cpu className="w-5 h-5 text-purple-600" />
                Gemini Model Selection
              </CardTitle>
              <CardDescription className="text-xs">
                Choose the Google Gemini AI model for email processing
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { id: 'gemini-2.0-flash', name: 'Gemini 2.0 Flash', badge: 'Recommended', desc: 'Next-gen speed, ultra low latency, best for customer email replies' },
                { id: 'gemini-1.5-pro', name: 'Gemini 1.5 Pro', badge: 'Complex Reasoning', desc: 'Deep contextual understanding for multi-page complex disputes' },
                { id: 'gemini-1.5-flash', name: 'Gemini 1.5 Flash', badge: 'Balanced', desc: 'Standard production model with fast response times' },
              ].map((model) => (
                <div
                  key={model.id}
                  onClick={() => setSelectedModel(model.id)}
                  className={`p-4 rounded-xl border cursor-pointer transition-all flex flex-col justify-between ${
                    selectedModel === model.id
                      ? 'border-blue-500 bg-blue-50/50 ring-1 ring-blue-500'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                  }`}
                >
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-bold text-gray-900">{model.name}</h4>
                      {selectedModel === model.id && <CheckCircle2 className="w-4 h-4 text-blue-600" />}
                    </div>
                    <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-purple-100 text-purple-800 border border-purple-200">
                      {model.badge}
                    </span>
                    <p className="text-xs text-gray-500 mt-2">{model.desc}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'automation' && (
        <div className="space-y-6 max-w-4xl">
          <Card className="border-gray-100 shadow-sm">
            <CardHeader className="border-b border-gray-100">
              <CardTitle className="text-base font-bold text-gray-900 flex items-center gap-2">
                <Sliders className="w-5 h-5 text-emerald-600" />
                Auto-Reply & Confidence Thresholds
              </CardTitle>
              <CardDescription className="text-xs">
                Control human-in-the-loop review criteria and automation levels
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-5 space-y-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-bold text-gray-900">AI Confidence Score Threshold</h4>
                    <p className="text-xs text-gray-500">Emails above this confidence level will be auto-approved</p>
                  </div>
                  <span className="text-lg font-extrabold text-blue-700 bg-blue-50 border border-blue-200 px-3 py-1 rounded-xl">
                    {confidenceThreshold}%
                  </span>
                </div>
                <input
                  type="range"
                  min="50"
                  max="95"
                  step="5"
                  value={confidenceThreshold}
                  onChange={(e) => setConfidenceThreshold(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
              </div>

              <div className="pt-4 border-t border-gray-100 space-y-2">
                <h4 className="text-sm font-bold text-gray-900">Background Poll Interval</h4>
                <p className="text-xs text-gray-500">Frequency to check Gmail & eBay for new messages</p>
                <div className="flex gap-3 pt-1">
                  {['30', '60', '120', '300'].map((sec) => (
                    <button
                      key={sec}
                      onClick={() => setPollInterval(sec)}
                      className={`px-4 py-2 text-xs font-bold rounded-xl border transition-all ${
                        pollInterval === sec
                          ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                          : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      Every {sec}s
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'channels' && (
        <div className="space-y-6 max-w-4xl">
          <Card className="border-gray-100 shadow-sm">
            <CardHeader className="border-b border-gray-100">
              <CardTitle className="text-base font-bold text-gray-900 flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-600" />
                Connected Email Accounts
              </CardTitle>
              <CardDescription className="text-xs">
                Add or remove the mailboxes eDesk reads customer messages from
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-5 space-y-4">
              {connectedEmails.length === 0 && (
                <div className="p-4 text-center text-xs text-gray-500 bg-gray-50 border border-dashed border-gray-200 rounded-xl">
                  No email accounts connected yet. Add one below.
                </div>
              )}

              {connectedEmails.map((acc: any) => (
                <div key={acc.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-white border border-gray-200 rounded-xl shadow-2xs gap-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-blue-50 text-blue-600 rounded-xl">
                      <Mail className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-gray-900">{acc.email}</h4>
                      <p className="text-xs text-gray-500 capitalize">
                        {acc.provider}{acc.has_password ? ' · app password set' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleEmail(acc.id, acc.active)}
                      className={`px-3 py-1 text-xs font-bold rounded-full border flex items-center gap-1.5 transition-colors ${
                        acc.active
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                          : 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200'
                      }`}
                    >
                      <span className={`w-2 h-2 rounded-full ${acc.active ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                      {acc.active ? 'Active' : 'Paused'}
                    </button>
                    <Button
                      onClick={() => handleRemoveEmail(acc.id, acc.email)}
                      variant="outline"
                      size="sm"
                      className="text-xs border-red-200 text-red-600 hover:bg-red-50 flex items-center gap-1.5"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      <span>Remove</span>
                    </Button>
                  </div>
                </div>
              ))}

              <div className="pt-3 border-t border-gray-100 space-y-2">
                <label className="text-xs font-bold text-gray-700">Add an email account</label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="inbox@example.com"
                    className="text-xs"
                  />
                  <select
                    value={newProvider}
                    onChange={(e) => setNewProvider(e.target.value)}
                    className="text-xs border border-gray-200 rounded-lg px-3 py-2 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="gmail">Gmail</option>
                    <option value="imap">IMAP</option>
                    <option value="forwarding">Forwarding</option>
                  </select>
                  <Input
                    type="password"
                    value={newAppPassword}
                    onChange={(e) => setNewAppPassword(e.target.value)}
                    placeholder="App password (optional)"
                    className="text-xs font-mono"
                  />
                  <Button
                    onClick={handleAddEmail}
                    disabled={isAddingEmail}
                    className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-4 flex items-center gap-1.5"
                  >
                    {isAddingEmail ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                    <span>Add</span>
                  </Button>
                </div>
                <p className="text-[11px] text-gray-400">
                  The app password is stored securely and never shown again. Reading mail from added mailboxes is wired in a later step — the primary inbox still uses the server settings.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-gray-100 shadow-sm">
            <CardHeader className="border-b border-gray-100">
              <CardTitle className="text-base font-bold text-gray-900 flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-red-600" />
                eBay Marketplace Channel
              </CardTitle>
              <CardDescription className="text-xs">
                OAuth 2.0 integration for eBay Seller Messages & Customer Inquiries
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-5 space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-white border border-gray-200 rounded-xl gap-4 shadow-2xs">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-red-50 text-red-600 rounded-xl">
                    <ShoppingCart className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-gray-900">Electronicx eBay Store</h4>
                    <p className="text-xs text-gray-500">eBay Messaging API (OAuth 2.0 Token Sync)</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    onClick={handleConnectEbay}
                    variant="outline"
                    size="sm"
                    className="text-xs border-red-200 text-red-700 hover:bg-red-50 flex items-center gap-1.5"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    <span>OAuth Connect</span>
                  </Button>

                  <Button
                    onClick={handleTestEbay}
                    variant="outline"
                    size="sm"
                    className="text-xs border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-1.5"
                  >
                    <Zap className="w-3.5 h-3.5 text-amber-500" />
                    <span>Test API</span>
                  </Button>

                  <Button
                    onClick={handleSyncEbay}
                    disabled={isSyncingEbay}
                    size="sm"
                    className="bg-red-600 hover:bg-red-700 text-white text-xs font-bold flex items-center gap-1.5 shadow-xs"
                  >
                    {isSyncingEbay ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <RefreshCw className="w-3.5 h-3.5" />
                    )}
                    <span>Sync eBay</span>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
